import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Zap,
  Sun,
  ShieldCheck,
  Radio,
  Gauge,
  Cpu,
  Boxes,
  Server,
  Cable,
  MapPin,
  Check,
  Sparkles,
  Landmark,
  Palette,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { useI18n } from "@/lib/i18n";

const API = "/api";

const STREET_IMAGE = "/images/liber-blocker-b1-street.webp";
const PRODUCT_IMAGE = "/images/liber-blocker-b1-product.webp";

const Overline = ({ children, testId }) => (
  <div
    data-testid={testId}
    className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#C2674A] mb-6 flex items-center gap-3"
  >
    <span className="inline-block w-6 h-px bg-[#C2674A]" />
    {children}
  </div>
);

/* ---------- HERO ---------- */
const Hero = () => {
  const { t } = useI18n();
  return (
    <section
      id="hero"
      className="relative pt-24 pb-16 md:pt-28 md:pb-24 grain overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none opacity-[0.08]">
        <div
          className="w-full h-full"
          style={{
            backgroundImage:
              "linear-gradient(to right, #3A3E43 1px, transparent 1px), linear-gradient(to bottom, #3A3E43 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
      </div>

      <div className="relative max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-12">
          <div>
            <Overline testId="hero-overline">{t.hero.overline}</Overline>
            <h1
              data-testid="hero-title"
              className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95]"
            >
              {t.hero.title1}{" "}
              <span className="text-[#C2674A]">{t.hero.titleHighlight}</span>
              {t.hero.title2}
            </h1>
            <p
              data-testid="hero-subtitle"
              className="mt-8 text-lg md:text-xl text-[#A0A0A0] max-w-3xl leading-relaxed"
            >
              {t.hero.subtitle}
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 px-6 py-3.5 bg-[#C2674A] text-black hover:bg-[#A8553A] transition-all duration-200 font-semibold"
              >
                {t.hero.ctaPrimary}
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>
              <Link
to="/platform"
                data-testid="hero-cta-secondary"
                className="group inline-flex items-center gap-2 px-6 py-3.5 border border-[#3A3E43] text-white hover:border-[#C2674A] hover:text-[#C2674A] transition-all duration-200 font-medium"
              >
                {t.hero.ctaSecondary}
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg border-t border-[#3A3E43] pt-8">
              {[
                { v: t.hero.stat1, l: t.hero.stat1Label },
                { v: t.hero.stat2, l: t.hero.stat2Label },
                { v: t.hero.stat3, l: t.hero.stat3Label },
              ].map((s, i) => (
                <div key={i} data-testid={`hero-stat-${i}`}>
                  <div className="font-heading text-2xl md:text-3xl font-bold text-white">
                    {s.v}
                  </div>
                  <div className="mt-1 text-xs text-[#A0A0A0] font-mono tracking-wide uppercase leading-tight">
                    {s.l.split("|").map((line, idx) => (
                      <span key={idx} className="block">{line}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Visual — real product photo, full-width below text */}
          <div className="w-full max-w-5xl">
            <div className="relative aspect-[4/3] sm:aspect-[25/16] border border-[#3A3E43] bg-[#23262A] overflow-hidden group">
              <img
                src={STREET_IMAGE}
                alt="LìBER smart parking blocker"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
                
              />
              {/* Subtle top vignette so corner ticks + labels stay readable */}
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/40 via-transparent to-black/60" />

              {/* Readouts */}
              <div className="absolute top-4 left-4 right-4 flex items-start justify-between font-mono text-[10px] tracking-[0.18em] uppercase">
                <div className="text-white/90">
                  <div className="text-[#C2674A]">LìBER / BLOCKER-01</div>
                  <div className="mt-1 text-white/70">Status · Armed</div>
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <span className="w-2 h-2 rounded-full bg-[#C2674A] pulse-dot" />
                  Live
                </div>
              </div>

              {/* Bottom spec strip */}
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between font-mono text-[10px] tracking-[0.18em] uppercase text-white/80">
                <div>
                  <div className="text-[#C2674A]">Model · B1</div>
                  <div className="mt-1">Concrete composite · IP67</div>
                </div>
                <div className="hidden sm:block text-right text-white">
                  <div>465 × 235 × 65</div>
                  <div className="text-white/60">millimetres</div>
                </div>
              </div>

              {/* Corner ticks */}
              <div className="absolute top-3 left-3 w-4 h-4 border-l border-t border-[#C2674A]" />
              <div className="absolute top-3 right-3 w-4 h-4 border-r border-t border-[#C2674A]" />
              <div className="absolute bottom-3 left-3 w-4 h-4 border-l border-b border-[#C2674A]" />
              <div className="absolute bottom-3 right-3 w-4 h-4 border-r border-b border-[#C2674A]" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- MARQUEE ---------- */
const Marquee = () => {
  const words = [
    "SMART BLOCKER",
    "MODULAR",
    "LoRa · BLE · LTE",
    "Wi-Fi · ETH",
    "SWAPPABLE BATTERY · PoE",
    "IP67",
    "REAL-TIME",
    "ANTI-THEFT",
    "HaaS",
    "OPEN API",
  ];
  const track = [...words, ...words];
  return (
    <div className="border-y border-[#3A3E43] py-6 overflow-hidden">
      <div className="marquee-track flex gap-12 whitespace-nowrap">
        {track.map((w, i) => (
          <span
            key={i}
            className="font-heading text-2xl md:text-3xl font-black tracking-tight text-[#A0A0A0] flex items-center gap-12"
          >
            {w}
            <span className="w-2 h-2 bg-[#C2674A]" />
          </span>
        ))}
      </div>
    </div>
  );
};

/* ---------- PROBLEM ---------- */
const Problem = () => {
  const { t } = useI18n();
  return (
    <section id="problem" className="py-24 md:py-32 border-t border-[#3A3E43]">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Overline testId="problem-overline">{t.problem.overline}</Overline>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <h2
              data-testid="problem-title"
              className="lg:col-span-7 font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]"
            >
              {t.problem.title}
            </h2>
            <p className="lg:col-span-5 text-lg text-[#A0A0A0] leading-relaxed self-end">
              {t.problem.body}
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#3A3E43] border border-[#3A3E43]">
          {t.problem.stats.map((s, i) => (
            <Reveal key={i} delay={i * 90}>
              <div
                data-testid={`problem-stat-${i}`}
                className="bg-[#1A1C1F] p-8 md:p-10 h-full"
              >
                <div className="font-heading text-5xl lg:text-6xl font-bold text-[#C2674A] whitespace-nowrap">
                  {s.value}
                </div>
                <p className="mt-4 text-sm md:text-base text-[#A0A0A0] leading-relaxed">
                  {s.label}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- SOLUTION ---------- */
const Solution = () => {
  const { t } = useI18n();
  const icons = [Boxes, Gauge, Radio, Cable, ShieldCheck, Cpu];
  return (
    <section id="solution" className="py-24 md:py-32 border-t border-[#3A3E43] bg-[#1E2023]">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Overline testId="solution-overline">{t.solution.overline}</Overline>
          <h2
            data-testid="solution-title"
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] max-w-4xl"
          >
            {t.solution.title}
          </h2>
          <p className="mt-6 text-lg text-[#A0A0A0] max-w-2xl leading-relaxed">
            {t.solution.body}
          </p>
        </Reveal>

        <div className="mt-16 max-w-5xl flex flex-col gap-px bg-[#3A3E43] border border-[#3A3E43]">
          {/* Image cell — same crop as Hero & Design */}
          <div className="w-full bg-[#23262A] relative aspect-[4/3] sm:aspect-[25/16] overflow-hidden">
            <img
              src={PRODUCT_IMAGE}
              alt="LìBER smart parking blocker — product shot"
              className="absolute inset-0 w-full h-full object-cover"
              
            />
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-[#1A1C1F]/85 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 flex justify-between items-end gap-4 font-mono text-[10px] sm:text-xs text-white/85">
              <div>
                <div className="text-[#C2674A] mb-1">LìBER · MODEL B1</div>
                <div>Smart parking blocker — ground-mounted, IP67</div>
              </div>
              <div className="hidden sm:block text-right">
                <div className="text-white">465 × 235 × 65</div>
                <div className="text-white/60">millimetres</div>
              </div>
            </div>
          </div>

          {/* Specs bento — full width below image */}
          <div className="grid grid-cols-2 md:grid-cols-3 bg-[#3A3E43] gap-px">
            {t.solution.specs.map((s, i) => {
              const Icon = icons[i % icons.length];
              return (
                <div
                  key={i}
                  data-testid={`solution-spec-${i}`}
                  className="bg-[#23262A] p-6 flex flex-col min-h-[160px]"
                >
                  <Icon size={20} className="text-[#C2674A]" />
                  {/* Top-aligned so titles and values line up across the row */}
                  <div className="mt-10">
                    <div className="text-xs font-mono text-[#A0A0A0] uppercase tracking-wider mb-1">
                      {s.k}
                    </div>
                    <div className="font-heading text-lg font-semibold text-white leading-tight">
                      {s.v}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- DESIGN ---------- */
const Design = () => {
  const { t } = useI18n();
  const variantIcons = [Sparkles, Landmark, Palette];
  return (
    <section id="design" className="py-24 md:py-32 border-t border-[#3A3E43]">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-12">
          <Reveal>
            <Overline testId="design-overline">{t.design.overline}</Overline>
            <h2
              data-testid="design-title"
              className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]"
            >
              {t.design.title}
            </h2>
            <p className="mt-6 text-lg text-[#A0A0A0] max-w-3xl leading-relaxed">
              {t.design.body}
            </p>
          </Reveal>

          <Reveal delay={80}>
            <div className="w-full max-w-5xl">
              <div className="relative aspect-[4/3] sm:aspect-[25/16] border border-[#3A3E43] bg-[#23262A] overflow-hidden">
                <img
                  src={STREET_IMAGE}
                  alt="LìBER blocker — urban setting"
                  className="absolute inset-0 w-full h-full object-cover"
                  
                />
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#1A1C1F]/40 via-transparent to-transparent" />

                {/* Annotation callouts */}
                <div className="absolute top-6 left-6 font-mono text-[10px] tracking-[0.18em] uppercase text-white/80 hidden sm:flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#C2674A]" />
                  Flowing lines
                </div>
                <div className="absolute bottom-6 right-6 font-mono text-[10px] tracking-[0.18em] uppercase text-white/80 hidden sm:flex items-center gap-2">
                  Concrete-composite base
                  <span className="w-1.5 h-1.5 bg-[#C2674A]" />
                </div>

                {/* Corner ticks */}
                <div className="absolute top-3 left-3 w-4 h-4 border-l border-t border-[#C2674A]" />
                <div className="absolute top-3 right-3 w-4 h-4 border-r border-t border-[#C2674A]" />
                <div className="absolute bottom-3 left-3 w-4 h-4 border-l border-b border-[#C2674A]" />
                <div className="absolute bottom-3 right-3 w-4 h-4 border-r border-b border-[#C2674A]" />
              </div>
            </div>
          </Reveal>

          <Reveal delay={140}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#3A3E43] border border-[#3A3E43]">
              {t.design.variants.map((v, i) => {
                const Icon = variantIcons[i % variantIcons.length];
                return (
                  <div
                    key={i}
                    data-testid={`design-variant-${i}`}
                    className="bg-[#23262A] p-5 flex items-start gap-4 hover:bg-[#272A2E] transition-colors duration-200"
                  >
                    <div className="w-10 h-10 border border-[#3A3E43] flex items-center justify-center text-[#C2674A] shrink-0">
                      <Icon size={16} />
                    </div>
                    <div>
                      <div className="font-heading text-base font-semibold text-white">
                        {v.k}
                      </div>
                      <div className="mt-1 text-sm text-[#A0A0A0] leading-relaxed">
                        {v.d}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ---------- HOW IT WORKS ---------- */
const HowItWorks = () => {
  const { t } = useI18n();
  return (
    <section id="how" className="py-24 md:py-32 border-t border-[#3A3E43]">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Overline testId="how-overline">{t.how.overline}</Overline>
          <h2
            data-testid="how-title"
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] max-w-3xl"
          >
            {t.how.title}
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#3A3E43] border border-[#3A3E43]">
          {t.how.steps.map((s, i) => (
            <Reveal key={i} delay={i * 80}>
              <div
                data-testid={`how-step-${i}`}
                className="bg-[#1A1C1F] p-8 h-full flex flex-col gap-6 min-h-[260px]"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-[#C2674A] tracking-wider">
                    {s.n}
                  </span>
                  <span className="h-px flex-1 ml-4 bg-[#3A3E43]" />
                  <ArrowRight size={14} className="text-[#3A3E43] ml-2" />
                </div>
                <div>
                  <h3 className="font-heading text-2xl font-semibold text-white mb-3">
                    {s.t}
                  </h3>
                  <p className="text-sm text-[#A0A0A0] leading-relaxed">{s.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- FEATURES ---------- */
const Features = () => {
  const { t } = useI18n();
  const icons = [Boxes, Radio, Zap, ShieldCheck, Gauge, Server];
  return (
    <section id="features" className="py-24 md:py-32 border-t border-[#3A3E43] bg-[#1E2023]">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Overline testId="features-overline">{t.features.overline}</Overline>
          <h2
            data-testid="features-title"
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] max-w-3xl"
          >
            {t.features.title}
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#3A3E43] border border-[#3A3E43]">
          {t.features.items.map((f, i) => {
            const Icon = icons[i % icons.length];
            return (
              <Reveal key={i} delay={i * 60}>
                <div
                  data-testid={`feature-${i}`}
                  className="bg-[#23262A] p-8 h-full group hover:bg-[#272A2E] transition-colors duration-300 min-h-[220px] flex flex-col gap-6"
                >
                  <div className="w-10 h-10 border border-[#3A3E43] flex items-center justify-center text-[#C2674A] group-hover:border-[#C2674A] transition-colors">
                    <Icon size={18} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-white mb-2">
                      {f.t}
                    </h3>
                    <p className="text-sm text-[#A0A0A0] leading-relaxed">{f.d}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------- MARKET ---------- */
const Market = () => {
  const { t } = useI18n();
  return (
    <section id="market" className="py-24 md:py-32 border-t border-[#3A3E43]">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Overline testId="market-overline">{t.market.overline}</Overline>
          <h2
            data-testid="market-title"
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] max-w-3xl"
          >
            {t.market.title}
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#3A3E43] border border-[#3A3E43]">
          {t.market.stats.map((s, i) => (
            <Reveal key={i} delay={i * 80}>
              <div
                data-testid={`market-stat-${i}`}
                className="bg-[#1A1C1F] p-8 md:p-10 flex flex-col justify-between min-h-[280px]"
              >
                <div className="font-mono text-[11px] text-[#A0A0A0] uppercase tracking-[0.18em] leading-snug">
                  {s.k}
                </div>
                <div>
                  <div className="font-heading text-5xl md:text-6xl font-bold text-white">
                    {s.v}
                  </div>
                  {s.sub && (
                    <div className="mt-2 text-sm text-[#D98164] font-medium">
                      {s.sub}
                    </div>
                  )}
                  <div className="mt-3 text-sm text-[#A0A0A0] leading-relaxed">
                    {s.d}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-px grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#3A3E43] border border-[#3A3E43] border-t-0">
          <div className="bg-[#1A1C1F] p-8 md:p-10">
            <div className="font-mono text-xs text-[#C2674A] uppercase tracking-[0.2em] mb-6">
              {t.market.streamsTitle}
            </div>
            <ul className="space-y-4">
              {t.market.streams.map((s, i) => (
                <li
                  key={i}
                  data-testid={`market-stream-${i}`}
                  className="flex items-start gap-3 text-[#E5E5E5]"
                >
                  <Check size={18} className="text-[#C2674A] mt-0.5 shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[#1A1C1F] p-8 md:p-10">
            <div className="font-mono text-xs text-[#C2674A] uppercase tracking-[0.2em] mb-6">
              {t.market.pricingTitle}
            </div>
            <div className="divide-y divide-[#3A3E43] border-y border-[#3A3E43]">
              {t.market.pricing.map((p, i) => (
                <div
                  key={i}
                  data-testid={`market-pricing-${i}`}
                  className="flex items-center justify-between py-4"
                >
                  <span className="text-[#A0A0A0]">{p.k}</span>
                  <span className="font-heading text-xl font-semibold text-white">
                    {p.v}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- TEAM ---------- */
const Team = () => {
  const { t } = useI18n();
  const portraits = [
    "/images/team-1.webp",
    "/images/team-2.webp",
    "/images/team-3.webp",
  ];
  return (
    <section id="team" className="py-24 md:py-32 border-t border-[#3A3E43] bg-[#1E2023]">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Overline testId="team-overline">{t.team.overline}</Overline>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <h2
              data-testid="team-title"
              className="lg:col-span-7 font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]"
            >
              {t.team.title}
            </h2>
            <p className="lg:col-span-5 text-lg text-[#A0A0A0] leading-relaxed">
              {t.team.body}
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.team.members.map((m, i) => (
            <Reveal key={i} delay={i * 80} className="h-full">
              <div
                data-testid={`team-member-${i}`}
                className="group border border-[#3A3E43] bg-[#23262A] overflow-hidden h-full flex flex-col"
              >
                <div className="aspect-[4/5] overflow-hidden bg-[#2E3136]">
                  <img
                    src={portraits[i % portraits.length]}
                    alt={m.n}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 scale-105 group-hover:scale-100"
                  />
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="font-heading text-lg font-semibold text-white">
                    {m.n}
                  </div>
                  <div className="text-xs text-[#C2674A] font-mono tracking-wider uppercase mt-1">
                    {m.r}
                  </div>
                  <p className="mt-3 text-sm text-[#A0A0A0] leading-relaxed">
                    {m.b}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- CONTACT ---------- */
const Contact = () => {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "",
    company: "",
    message: "",
    website: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/contacts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role || "Other",
        company: form.company.trim() || null,
        message: form.message.trim(),
          website: form.website,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", role: "", company: "", message: "", website: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#23262A] border border-[#3A3E43] px-4 py-3 text-white placeholder:text-[#555] focus:border-[#C2674A] transition-colors duration-200 font-sans";

  return (
    <section id="contact" className="py-24 md:py-32 border-t border-[#3A3E43]">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <Overline testId="contact-overline">{t.contact.overline}</Overline>
            <h2
              data-testid="contact-title"
              className="font-heading text-5xl md:text-6xl font-bold tracking-tighter leading-none"
            >
              {t.contact.title}
            </h2>
            <p className="mt-8 text-lg text-[#A0A0A0] leading-relaxed max-w-md">
              {t.contact.body}
            </p>
            <div className="mt-12 space-y-4 font-mono text-sm text-[#A0A0A0]">
              <div className="flex items-center gap-3">
                <MapPin size={14} className="text-[#C2674A]" /> Brasov · Timisoara · Fulda · Remote
              </div>
              <div className="flex items-center gap-3">
                <Radio size={14} className="text-[#C2674A]" /> contact@liberparking.eu
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={120}>
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                required
                data-testid="contact-input-name"
                value={form.name}
                onChange={update("name")}
                placeholder={t.contact.name}
                className={`${inputCls} sm:col-span-1`}
              />
              <input
                required
                type="email"
                data-testid="contact-input-email"
                value={form.email}
                onChange={update("email")}
                placeholder={t.contact.email}
                className={`${inputCls} sm:col-span-1`}
              />
              <select
                required
                data-testid="contact-input-role"
                value={form.role}
                onChange={update("role")}
                className={`${inputCls} appearance-none`}
              >
                <option value="" disabled>
                  {t.contact.role}
                </option>
                {t.contact.roles.map((r) => (
                  <option key={r} value={r} className="bg-[#23262A]">
                    {r}
                  </option>
                ))}
              </select>
              <input
                data-testid="contact-input-company"
                value={form.company}
                onChange={update("company")}
                placeholder={t.contact.company}
                className={inputCls}
              />
              <textarea
                required
                rows={5}
                data-testid="contact-input-message"
                value={form.message}
                onChange={update("message")}
                placeholder={t.contact.message}
                className={`${inputCls} sm:col-span-2 resize-none`}
              />
            {/* Honeypot: invisible to people, bots fill it in */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={form.website}
              onChange={update("website")}
              className="absolute -left-[9999px] w-px h-px opacity-0"
            />
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="sm:col-span-2 inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#C2674A] text-black hover:bg-[#A8553A] transition-colors duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t.contact.submitting : t.contact.submit}
                {!loading && <ArrowRight size={16} />}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Landing = () => {
  return (
    <div className="min-h-screen bg-[#1A1C1F] text-white">
      <Toaster position="bottom-right" richColors theme="dark" />
      <Header />
      <main data-testid="landing-main">
        <Hero />
        <Marquee />
        <Problem />
        <Solution />
        <Design />
        <HowItWorks />
        <Features />
        <Market />
        <Team />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

export default Landing;
